/** Reading guidance only. Completion and permissions always come from the API. */
export const GUIDE_STEPS = [
  "client",
  "specification",
  "authorization",
  "census",
  "insurers",
  "review",
  "followup",
] as const;
export type GuideStep = (typeof GUIDE_STEPS)[number];
type Chapter = { title: string; body: string; action: string; exception: string };
export const guidedHelp: Record<
  "fr" | "en",
  { title: string; intro: string; history: Chapter; steps: Record<GuideStep, Chapter> }
> = {
  fr: {
    history: {
      title: "Consulter l’historique et le suivi",
      body: "Cette demande n’est plus en préparation. Consultez son état, ses documents conservés et les traces de livraison existantes. Une demande annulée ne doit pas être envoyée.",
      action: "Consulter le dossier et son historique",
      exception:
        "Une trace absente ne prouve pas un envoi. Consultez les événements datés ; le guide ne relance ni ne réactive la demande.",
    },
    title: "Votre parcours, une étape à la fois",
    intro:
      "Le cahier, le mandat et l’effectif avancent en parallèle. Ce guide vous oriente ; seul l’état réel du dossier détermine ce qui est prêt.",
    steps: {
      client: {
        title: "Démarrer le dossier sans contact",
        body: "Vérifiez le cabinet, réutilisez l’entreprise et le dossier existants. Dans Étape de soumission, choisissez la demande à reprendre. Dans Assureurs, Reprendre le brouillon ne crée rien ; Créer une autre demande démarre un brouillon distinct. Une nouvelle demande démarre avec l’entreprise et un titre, sans contact ; les coordonnées viendront avant la demande au client.",
        action: "Voir le client et ses contacts",
        exception:
          "Pour un prospect sans demande, créez seulement son entreprise dans Clients. Si l’entreprise manque dans un dossier, ajoutez-la depuis Nouvelle demande ou Clients. Aucun contact n’est nécessaire pour ce brouillon. Ne recréez pas une identité pour changer de cabinet.",
      },
      specification: {
        title: "Préparer le cahier du courtier",
        body: "Reprenez le cahier de cette demande ou choisissez un modèle publié approprié. Vérifiez garanties, paramètres et dates. Le mandat peut avancer en parallèle.",
        action: "Ouvrir le cahier de cette demande",
        exception:
          "Si le modèle manque, consultez la bibliothèque de formulaires. Créer et publier un modèle exige les droits du cabinet ; demandez à votre administrateur si nécessaire. Rien n’est publié par le guide.",
      },
      authorization: {
        title: "Vérifier le mandat du client",
        body: "Conservez le mandat signé valide de ce dossier ou consultez son invitation existante. Un nouveau dossier exige son propre mandat. Le courriel livré et la signature du client sont deux états distincts.",
        action: "Voir le mandat de ce dossier",
        exception:
          "Sans mandat, choisissez une version publiée et le contact de l’entreprise. Ne signez pas à la place du client. Vous pouvez continuer le cahier et l’effectif en attendant.",
      },
      census: {
        title: "Contrôler l’effectif et son export",
        body: "La remise du client est actualisée au retour dans l’onglet et pendant la consultation. Relisez les lignes reçues, terminez puis verrouillez la liste. Examinez l’export et sa fraîcheur avant l’envoi aux assureurs.",
        action: "Ouvrir l’effectif du dossier",
        exception:
          "Si l’export est périmé, vérifiez les changements avant de le régénérer. Remplacer la liste écrase la liste courante ; le guide ne déclenche pas cette opération.",
      },
      insurers: {
        title: "Préparer les destinataires",
        body: "Vérifiez pour chaque assureur l’adresse réelle, la langue, l’échéance, la commission et les notes. Les assureurs envisagés ne sont pas les destinataires d’envoi.",
        action: "Voir les destinataires de cette demande",
        exception:
          "Si un assureur manque, consultez le catalogue du cabinet. Les notes internes, les notes du PDF et celles du courriel ont des usages distincts.",
      },
      review: {
        title: "Relire chaque courriel et ses pièces",
        body: "Pour chaque destinataire, ouvrez le sujet, le texte, l’aperçu et les pièces exactes. Les blocages affichés proviennent du serveur. Pour le logo des futurs documents, un administrateur choisit un PNG/JPEG dans Paramètres du cabinet, puis enregistre ; les documents déjà produits restent inchangés.",
        action: "Ouvrir la relecture par destinataire",
        exception:
          "Une pièce absente ou périmée se corrige à sa source. Suivant change la page du guide : il ne valide aucune pièce et ne lève aucun blocage.",
      },
      followup: {
        title: "Envoyer explicitement, puis suivre",
        body: "L’envoi exige une action explicite après relecture. Suivez ensuite la livraison et les offres reçues. Une demande annulée reste consultable dans l’historique.",
        action: "Ouvrir les contrôles et le suivi",
        exception:
          "En cas d’incertitude, vérifiez l’état avant tout renvoi. L’aide n’envoie aucun courriel. L’import automatique des réponses par courriel n’est pas disponible.",
      },
    },
  },
  en: {
    history: {
      title: "View history and follow-up",
      body: "This request is no longer being prepared. Review its status, preserved documents and existing delivery records. A cancelled request must not be sent.",
      action: "View the project and its history",
      exception:
        "A missing record does not prove delivery. Review dated events; the guide does not resend or reactivate the request.",
    },
    title: "Your workflow, one step at a time",
    intro:
      "Specifications, the mandate and the employee census can progress in parallel. This guide provides directions; the actual project state determines what is ready.",
    steps: {
      client: {
        title: "Start the project without a contact",
        body: "Check the firm and reuse the existing company and project. In Quote request stage, choose the request to resume. Under Insurers, Resume the draft creates nothing; Create another request starts a separate draft. A new request starts with the company and a title, without a contact; collect details before asking the client.",
        action: "View the client and contacts",
        exception:
          "For a prospect without a request, create only the company under Clients. If the company is missing from a file, add it from New request or Clients. No contact is needed for this draft. Do not recreate an identity to change firms.",
      },
      specification: {
        title: "Prepare the broker specifications",
        body: "Resume this request’s specifications or choose an appropriate published template. Check benefits, settings and dates. The mandate can progress in parallel.",
        action: "Open this request’s specifications",
        exception:
          "If the template is missing, check the form library. Creating and publishing a template requires firm permissions; ask your administrator if needed. The guide publishes nothing.",
      },
      authorization: {
        title: "Check the client mandate",
        body: "Keep this project’s valid signed mandate or inspect its existing invitation. A new project requires its own mandate. Email delivery and the client’s signature are different states.",
        action: "View this project’s mandate",
        exception:
          "Without a mandate, choose a published version and the company contact. Never sign on the client’s behalf. Specifications and the census can proceed while waiting.",
      },
      census: {
        title: "Check the census and export",
        body: "Client submissions refresh when you return to the tab and while viewing it. Review the received rows, complete and lock the list. Review the export and its freshness before sending to insurers.",
        action: "Open the project census",
        exception:
          "If the export is outdated, check what changed before regenerating it. Replacing the list overwrites the current list; the guide does not trigger that operation.",
      },
      insurers: {
        title: "Prepare the recipients",
        body: "For each insurer, check the actual address, language, deadline, commission and notes. Planned insurers are not the send recipients.",
        action: "View this request’s recipients",
        exception:
          "If an insurer is missing, check the firm’s catalogue. Internal notes, PDF notes and email notes serve different purposes.",
      },
      review: {
        title: "Review each email and attachment",
        body: "For each recipient, open the subject, message, preview and exact attachments. The server supplies the displayed blockers. For the logo on future documents, an administrator selects a PNG/JPEG in firm settings and saves it; existing documents remain unchanged.",
        action: "Open the recipient review",
        exception:
          "Fix a missing or outdated attachment at its source. Next changes the guide page; it does not validate attachments or remove blockers.",
      },
      followup: {
        title: "Explicitly send, then follow up",
        body: "Sending requires an explicit action after review. Then track delivery and received offers. Cancelled requests remain available in history.",
        action: "Open the checks and follow-up",
        exception:
          "If uncertain, inspect the current state before resending. Help sends no email. Automatic importing of email replies is not available.",
      },
    },
  },
};

/** Concrete navigation instructions; these never decide readiness or grant permissions. */
export const guidedInstructions: Record<
  "fr" | "en",
  Record<GuideStep, { steps: string[]; missing: string[] }>
> = {
  fr: {
    client: {
      steps: [
        "Vérifiez le cabinet dans le sélecteur de l’en-tête. Ouvrez Clients et réutilisez l’entreprise existante.",
        "Ouvrez le dossier existant pour le reprendre. Sinon, Nouvelle demande demande seulement l’entreprise et un titre : Créer et préparer crée le brouillon sans contact, sans invitation ni courriel.",
        "Sur Nouvelle demande, Me montrer vise les champs de création, jamais un ancien dossier. Après création, l’aide suit le dossier et la demande créés. Pour un dossier existant, choisissez-le depuis Dossiers ou le sélecteur du guide lorsqu’il est proposé.",
        "Une page de dossier sans demande, comme Modifier, revient à la lecture du dossier. Sélectionnez de nouveau la demande dans ce panneau pour retrouver sa propre progression.",
      ],
      missing: [
        "Si l’entreprise manque, créez-la explicitement avec son nom. Préparez le cahier et l’effectif maintenant ; recueillez le courriel puis le vrai nom du contact plus tard, avant une demande au client.",
        "Si l’accès au cabinet est en attente ou refusé, faites vérifier l’appartenance. Ne recréez pas une identité pour contourner le refus.",
      ],
    },
    specification: {
      steps: [
        "Ouvrez le cahier de la demande. Reprenez le brouillon existant ou choisissez une version publiée adaptée au cabinet.",
        "Le courtier complète les garanties, paramètres et dates. Enregistrez et contrôlez les données avant de terminer le cahier.",
        "Relisez le PDF rattaché à la bonne demande. Le mandat et l’effectif peuvent avancer en parallèle ; lire cette étape ne les valide pas.",
      ],
      missing: [
        "Ouvrez la bibliothèque de formulaires et cherchez d’abord le modèle existant.",
        "S’il manque et que vos droits le permettent, choisissez Nouveau formulaire. Renseignez le code, le nom, la langue et les renseignements facultatifs, puis Créer le formulaire : le brouillon v1 est créé automatiquement. Ouvrez Modifier le brouillon, choisissez l’usage Cahier de charges, configurez les sections, champs et validations puis Enregistrer.",
        "Pour un texte d’aide ou indicatif facultatif, renseignez le français et l’anglais, ou laissez les deux vides. À l’enregistrement, choisissez une erreur pour rejoindre le champ à corriger ; votre saisie est conservée.",
        "Contrôlez le brouillon et son aperçu. Publier est une action distincte soumise aux droits et aux contrôles du serveur ; un brouillon n’est pas un modèle publié.",
        "Revenez au dossier par le panneau, choisissez explicitement la version publiée pour ce cahier et reprenez la saisie. Conservez vos modifications avant tout changement de page.",
      ],
    },
    authorization: {
      steps: [
        "Dans Mandat, vérifiez si un mandat signé valide est déjà conservé pour ce dossier et ouvrez le PDF conservé.",
        "Sinon, consultez d’abord l’invitation existante : contact, version, dates et livraison. Sans envoi, préparez la première demande ; une invitation préparée attend votre envoi explicite. En file ou acceptée ne signifie pas livrée. Vérifiez un échec ou un état inconnu avant toute relance ; une réponse reçue reste à contrôler si le requis manque.",
        "Sans invitation adéquate, choisissez le mandat publié puis ouvrez la demande au client. Réutilisez un contact existant ; s’il manque son courriel, sélectionnez-le puis Compléter les coordonnées. Sinon Ajouter un contact demande le courriel avant le vrai nom. Enregistrer les coordonnées ne crée aucune invitation.",
        "Vérifiez le destinataire et le vrai signataire avant de cliquer explicitement Envoyer. Le client signe lui-même. Le cahier et l’effectif avancent en parallèle ; une livraison courriel n’est pas une signature.",
      ],
      missing: [
        "Un mandat d’un autre dossier ou un ancien mandat sans dossier n’autorise pas ce dossier. Consultez l’historique sans déplacer ni remplacer sa preuve ; demandez un mandat propre à ce dossier si nécessaire. Après la signature, la confirmation reste dans le même onglet. Si le lien devient ensuite indisponible après réouverture, vérifiez la réception auprès du courtier avant toute nouvelle invitation ou signature.",
        "Si aucun mandat publié n’est proposé, ouvrez la bibliothèque et recherchez le modèle du cabinet.",
        "Si le modèle manque et que vos droits le permettent, choisissez Nouveau formulaire puis Créer le formulaire après avoir renseigné ses informations : le brouillon v1 est créé automatiquement. Dans Modifier le brouillon, choisissez l’usage Mandat client, configurez les champs et le signataire à partir du texte approuvé du cabinet, puis Enregistrer. Faites revoir le modèle avant sa publication par une personne habilitée. L’aide ne publie rien.",
        "Revenez à cette demande et choisissez la version publiée. Recueillez le courriel puis le nom dans le dialogue client, ou complétez le contact existant sans doublon. Choisissez explicitement le destinataire ; aucun accès ni courriel n’est créé par le guide.",
      ],
    },
    census: {
      steps: [
        "Ouvrez Effectif. Réutilisez la liste du dossier ou créez-la explicitement si elle manque.",
        "En saisie, utilisez l’import du classeur puis contrôlez les lignes, valeurs et anomalies. Un import remplace la liste courante : relisez la confirmation.",
        "Déclarez la liste terminée puis verrouillez-la avec les contrôles prévus. Ces deux états sont distincts.",
        "Consultez l’export proposé et sa fraîcheur indiquée par le serveur. Régénérez-le seulement après vérification et par une action explicite.",
      ],
      missing: [
        "Si l’import n’est plus proposé, vérifiez le statut. Ne déverrouillez/remettez en saisie que si une correction est réellement nécessaire et autorisée.",
        "Un export absent ou périmé se corrige ici, pas en ignorant le blocage d’envoi.",
      ],
    },
    insurers: {
      steps: [
        "Dans Destinataires, ajoutez explicitement les assureurs retenus. La liste envisagée du dossier n’est pas la liste d’envoi.",
        "Pour chaque assureur coché, choisissez Français ou Anglais dans Langue du courriel. Ce choix ne change ni le registre du cabinet ni les pièces jointes. Contrôlez aussi l’adresse, les notes du courriel et la commission ; notes internes et PDF restent distinctes.",
        "Vérifiez et enregistrez l’échéance ainsi que les modifications de préparation. Aucun envoi ne se produit en changeant d’étape.",
      ],
      missing: [
        "Si un assureur ou une adresse manque, consultez le catalogue ou les champs de correction proposés avec vos droits.",
        "Une adresse bloquée ou un refus du serveur doit être résolu ; le panneau ne permet aucun contournement.",
      ],
    },
    review: {
      steps: [
        "Ouvrez la relecture. Pour chaque destinataire, vérifiez le sujet, le texte exact et l’aperçu du courriel.",
        "Ouvrez les pièces réellement prévues : cahier, mandat et effectif selon la politique du cabinet. Vérifiez la bonne demande et la fraîcheur des fichiers.",
        "Lisez tous les blocages serveur et corrigez-les à leur source. Enregistrez vos modifications avant tout envoi.",
      ],
      missing: [
        "Si l’aperçu ou une pièce manque, revenez à sa section. Ne supposez pas qu’un ancien export annulé est la pièce courante.",
        "Sans droits ou sans réponse serveur, aucune validation locale n’autorise l’envoi.",
      ],
    },
    followup: {
      steps: [
        "Sur un brouillon, revenez à la relecture et contrôlez les destinataires/pièces. Le bouton d’envoi reste soumis aux blocages serveur.",
        "L’envoi est toujours votre clic explicite. Me montrer ne clique jamais sur ce bouton.",
        "Pour un PDF reçu, utilisez Importer une réponse assureur. Avant tout dépôt, le lien de retour au dossier conserve votre demande et votre vue ; ouvrir la page ne crée aucune réponse.",
        "Après l’envoi, consultez les statuts datés par assureur. Une demande annulée ou expirée reste dans l’historique ; ne la relancez pas depuis l’aide.",
      ],
      missing: [
        "Si le bouton est désactivé, lisez les blocages et les modifications non enregistrées. Corrigez puis relisez.",
        "Le statut du dossier résume son avancement ; celui de la demande et les événements datés décrivent l’envoi. Livré ne veut pas dire offre reçue. Vérifiez l’historique avant toute décision de renvoi.",
      ],
    },
  },
  en: {
    client: {
      steps: [
        "Check the firm in the header selector. Open Clients and reuse the existing company.",
        "Open the existing project to resume it. Otherwise, New request needs only the company and a title: Create and prepare creates the draft without a contact, invitation or email.",
        "On New request, Show me targets the creation fields, never an earlier project. After creation, help follows the created project and request. For an existing project, open it from Projects or the guide selector when offered.",
        "A project page without a request, such as Edit, returns to project reading. Select the request again in this panel to resume its own reading progress.",
      ],
      missing: [
        "If the company is missing, explicitly create it with its name. Prepare the specifications and census now; collect the contact’s email and then real name later, before asking the client.",
        "For pending or denied firm access, have the membership checked. Do not recreate an identity to bypass the restriction.",
      ],
    },
    specification: {
      steps: [
        "Open this request’s specifications. Resume the existing draft or choose a published version appropriate for the firm.",
        "The broker completes benefits, settings and dates. Save and check the data before completing the specifications.",
        "Review the PDF attached to the correct request. The mandate and census can progress in parallel; reading this step does not validate them.",
      ],
      missing: [
        "Open the form library and first search for an existing template.",
        "If missing and permitted, choose New form. Enter the code, name, language and optional details, then Create form: draft v1 is created automatically. Open Edit draft, choose the Business specifications purpose, configure sections, fields and validations, then Save.",
        "For optional help or placeholder text, complete French and English or leave both empty. When saving, choose an error to reach the field to correct; your input is preserved.",
        "Review the draft and preview. Publishing is a separate action controlled by permissions and server checks; a draft is not a published template.",
        "Return to the project through the panel, explicitly select the published version for these specifications and resume entry. Preserve changes before leaving a page.",
      ],
    },
    authorization: {
      steps: [
        "In Mandate, check whether a valid signed mandate is already retained for this project and open its preserved PDF.",
        "Otherwise inspect the existing invitation first: contact, version, dates and delivery. With no send, prepare the first request; a prepared invitation awaits your explicit send. Queued or accepted does not mean delivered. Check a failure or unknown state before any follow-up; a received response still needs review if the requirement is missing.",
        "Without a suitable invitation, choose the published mandate and open the client request. Reuse an existing contact; if their email is missing, select them and Complete contact details. Otherwise Add contact asks for email before the real name. Saving details creates no invitation.",
        "Check the recipient and actual signatory before explicitly clicking Send. The client signs themselves. Specifications and census progress in parallel; email delivery is not a signature.",
      ],
      missing: [
        "A mandate from another project or a legacy mandate without a project does not authorize this project. Inspect the history without moving or replacing its evidence; request this project’s own mandate if needed. After signing, confirmation stays in the same tab. If reopening the link later shows it as unavailable, ask the broker to verify receipt before any new invitation or signature.",
        "If no published mandate is available, open the library and search for the firm’s template.",
        "If the template is missing and permitted, choose New form, enter its details, then Create form: draft v1 is created automatically. In Edit draft, choose the Client authorization purpose, configure fields and the signatory using the firm’s approved wording, then Save. Have the template reviewed before an authorized person publishes it. Help publishes nothing.",
        "Return to this request and choose the published version. Collect email then name in the client dialog, or complete the existing contact without a duplicate. Explicitly choose the recipient; help creates no access or email.",
      ],
    },
    census: {
      steps: [
        "Open Census. Reuse the project’s list or explicitly create one if missing.",
        "While editable, import the workbook and check rows, values and issues. Import replaces the current list: review the confirmation.",
        "Declare the list complete, then lock it through the provided controls. These are distinct states.",
        "Review the offered export and its server-reported freshness. Regenerate only after checking and through an explicit action.",
      ],
      missing: [
        "If import is no longer offered, check the status. Unlock and reopen entry only when a correction is necessary and authorized.",
        "Fix a missing or outdated export here rather than ignoring a sending blocker.",
      ],
    },
    insurers: {
      steps: [
        "In Recipients, explicitly add the selected insurers. The project’s planned insurer list is not the sending list.",
        "For each selected insurer, choose French or English in Email language. This changes neither the firm directory nor the attachments. Also check the address, email notes and commission; internal and PDF notes remain separate.",
        "Check and save the deadline and preparation edits. Changing steps never sends anything.",
      ],
      missing: [
        "If an insurer or address is missing, use the catalog or correction fields available with your permissions.",
        "Resolve blocked addresses or server refusals; the panel cannot bypass them.",
      ],
    },
    review: {
      steps: [
        "Open review. For each recipient, check the subject, exact text and email preview.",
        "Open the actual planned attachments: specifications, mandate and census according to firm policy. Check the request and file freshness.",
        "Read all server blockers and fix them at their source. Save your edits before any sending.",
      ],
      missing: [
        "If a preview or attachment is missing, return to its section. Do not treat an old cancelled export as the current attachment.",
        "Without permissions or a server response, no local check authorizes sending.",
      ],
    },
    followup: {
      steps: [
        "For a draft, return to review and check recipients and attachments. The send button remains subject to server blockers.",
        "Sending always requires your explicit click. Show me never clicks this button.",
        "For a received PDF, use Import an insurer response. Before uploading, the project breadcrumb preserves your request and view; opening the page creates no response.",
        "After sending, review dated insurer statuses. Cancelled or expired requests remain historical; do not restart them through help.",
      ],
      missing: [
        "If the button is disabled, read the blockers and unsaved changes. Fix them and review again.",
        "The project status summarizes progress; the request status and dated events describe sending. Delivered does not mean an offer was received. Check history before deciding to resend.",
      ],
    },
  },
};
